
// Import zstd decompression
import { decompress } from 'fzstd'

// Helper function to convert base64 to Uint8Array
function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

// Helper function to convert Uint8Array to base64
function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary)
}

// Helper function to concatenate Uint8Arrays
function concatenateUint8Arrays(...arrays: Uint8Array[]): Uint8Array {
  const totalLength = arrays.reduce((acc, arr) => acc + arr.length, 0)
  const result = new Uint8Array(totalLength)
  let offset = 0
  for (const arr of arrays) {
    result.set(arr, offset)
    offset += arr.length
  }
  return result
}


// Derive keys for encryption and MAC
async function deriveKeys(keyBase64: string): Promise<{ encKey: CryptoKey; macKey: Uint8Array }> {
  const keyBytes = base64ToUint8Array(keyBase64)
  
  // Create encoder for prefixes
  const encoder = new TextEncoder()
  
  // Derive encryption key using SHA-256
  const encKeyData = concatenateUint8Arrays(encoder.encode('ENC:'), keyBytes)
  const encKeyHash = await crypto.subtle.digest('SHA-256', encKeyData)
  const encKey = await crypto.subtle.importKey(
    'raw',
    encKeyHash,
    { name: 'AES-CBC' },
    false,
    ['encrypt', 'decrypt']
  )
  
  // Derive MAC key using SHA-256
  const macKeyData = concatenateUint8Arrays(encoder.encode('MAC:'), keyBytes)
  const macKeyHash = await crypto.subtle.digest('SHA-256', macKeyData)
  const macKey = new Uint8Array(macKeyHash)
  
  return { encKey, macKey }
}

// Compute HMAC-SHA256
async function computeHMAC(key: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, data)
  return new Uint8Array(signature)
}

// Verify HMAC
async function verifyHMAC(key: Uint8Array, data: Uint8Array, mac: Uint8Array): Promise<boolean> {
  const expectedMac = await computeHMAC(key, data)
  if (mac.length !== expectedMac.length) return false
  
  // Constant-time comparison
  let result = 0
  for (const [index, macByte] of mac.entries()) {
    result |= macByte ^ expectedMac[index]
  }
  return result === 0
}

export async function encryptMessage(message: string, keyBase64: string): Promise<string> {
  const { encKey, macKey } = await deriveKeys(keyBase64)
  
  // Generate random IV
  const iv = crypto.getRandomValues(new Uint8Array(16))
  
  // Encode message - let Web Crypto API handle padding automatically
  const encoder = new TextEncoder()
  const messageBytes = encoder.encode(message)
  
  // Use Web Crypto API's built-in PKCS7 padding by NOT specifying padding
  // AES-CBC automatically uses PKCS7 padding in Web Crypto API
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv },
    encKey,
    messageBytes  // Let Web Crypto handle padding
  )
  const ciphertextBytes = new Uint8Array(ciphertext)
  
  // Compute HMAC over IV + ciphertext
  const dataToMac = concatenateUint8Arrays(iv, ciphertextBytes)
  const mac = await computeHMAC(macKey, dataToMac)
  
  // Concatenate IV + ciphertext + MAC and return base64
  const result = concatenateUint8Arrays(iv, ciphertextBytes, mac)
  return uint8ArrayToBase64(result)
}

export async function decryptMessage(encrypted: string, keyBase64: string): Promise<string> {
  const { encKey, macKey } = await deriveKeys(keyBase64)
  
  const encryptedData = base64ToUint8Array(encrypted)
  
  // Check minimum length (16 bytes IV + 16 bytes min ciphertext + 32 bytes MAC)
  if (encryptedData.length < 64) {
    throw new Error('Invalid encrypted data length')
  }
  
  // Extract IV, ciphertext, and MAC
  const iv = encryptedData.slice(0, 16)
  const mac = encryptedData.slice(-32)
  const ciphertext = encryptedData.slice(16, -32)
  
  // Verify HMAC
  const dataToVerify = concatenateUint8Arrays(iv, ciphertext)
  const isValid = await verifyHMAC(macKey, dataToVerify, mac)
  if (!isValid) {
    throw new Error('HMAC verification failed')
  }
  
  // Decrypt - Web Crypto API automatically handles PKCS7 padding removal
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-CBC', iv },
    encKey,
    ciphertext
  )
  
  // Convert to string - padding is already removed by Web Crypto API
  const decoder = new TextDecoder()
  return decoder.decode(decrypted)
}

export async function encryptData(data: Record<string, unknown>, keyBase64: string): Promise<string> {
  // Generate a unique nonce (max 64 chars as per server requirements) 
  // Try a simpler format that matches server expectation
  const nonce = `unique-request-id-${Math.floor(Date.now() / 1000)}`
  
  // Create the exact structure the server expects - nonce and timestamp go inside data field
  const payload = {
    type: data.type,
    timeout: data.timeout,
    data: {
      nonce,
      timestamp: Date.now() / 1000, // Unix timestamp as float (like Python time.time())
      ...(typeof data.data === 'object' && data.data !== null ? data.data : {}) // Spread the original data fields
    }
  }
  
  // Create JSON string with no extra whitespace and validate it
  const message = JSON.stringify(payload)
  
  // JSON structure is now correct - nonce and timestamp are in data field
  
  // Defensive check: ensure JSON is valid and clean
  try {
    JSON.parse(message) // Validate the JSON
  } catch (error) {
    throw new Error(`Invalid JSON generated for encryption: ${String(error)}`)
  }
  
  // Check for non-printable characters that might cause parsing issues
  for (let i = 0; i < message.length; i++) {
    const charCode = message.charCodeAt(i)
    if (charCode < 32 && charCode !== 9 && charCode !== 10 && charCode !== 13) {
      throw new Error(`Invalid character at position ${i}: charCode=${charCode}`)
    }
  }
  
  return encryptMessage(message, keyBase64)
}

export async function decryptData(encrypted: string, keyBase64: string): Promise<Record<string, unknown> | string> {
  const decrypted = await decryptMessage(encrypted, keyBase64)
  
  // After AES decryption, the result is base64 encoded and zstd compressed
  // Step 1: Base64 decode the decrypted data
  let decodedData: string
  try {
    decodedData = atob(decrypted)
  } catch (error) {
    console.warn('Failed to base64 decode decrypted data, using as-is:', error)
    decodedData = decrypted
  }
  
  // Step 2: Convert to Uint8Array for zstd decompression
  const encodedBytes = new Uint8Array(decodedData.length)
  for (let i = 0; i < decodedData.length; i++) {
    encodedBytes[i] = decodedData.charCodeAt(i)
  }
  
  // Step 3: Zstd decompress
  let decompressedBytes: Uint8Array
  try {
    decompressedBytes = decompress(encodedBytes)
  } catch (error) {
    console.warn('Failed to zstd decompress data, using decoded data as-is:', error)
    // If decompression fails, use the base64 decoded data
    const finalData = decodedData
    return handleJsonParsing(finalData)
  }
  
  // Step 4: Convert decompressed bytes back to string
  const finalData = new TextDecoder().decode(decompressedBytes)
  
  return handleJsonParsing(finalData)
}

// Helper function to handle JSON parsing with metadata removal
function handleJsonParsing(data: string): Record<string, unknown> | string {
  // Check if the decrypted message contains our metadata fields
  try {
    const parsed: unknown = JSON.parse(data)
    
    // Only remove timestamp and nonce if they exist (for request decryption)
    if (typeof parsed === 'object' && parsed !== null && 'timestamp' in (parsed as { timestamp?: unknown }) && 'nonce' in (parsed as { nonce?: unknown })) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { timestamp, nonce, ...dataWithoutMeta } = parsed as { timestamp?: unknown; nonce?: unknown; [k: string]: unknown }
      // If there are no other fields, return the raw decrypted string
      if (Object.keys(dataWithoutMeta).length === 0) {
        return data
      }
      return dataWithoutMeta
    }
    
    // If no metadata fields, return parsed data
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
    return data
  } catch {
    // If not valid JSON, return the raw decrypted string
    return data
  }
}